import React, { useState } from 'react';
import { LoadingSpinner } from '@/components/ui/LoadingSpinner';
import { useToast } from '@/hooks/use-toast';
import {
  useEmailTemplates,
  useCreateEmailTemplate,
  useUpdateEmailTemplate,
  useDeleteEmailTemplate,
} from '@/hooks/useEmailTemplates';
import {
  TemplateCard,
  TemplateEmptyState,
  TemplateHeaderControls,
  TemplateEditorSheet,
  useTemplateFilterSort,
  SAMPLE_TEMPLATES,
  EmailTemplate,
  EmailTemplateFormData,
} from './templates/index';

const INITIAL_FORM_DATA: EmailTemplateFormData = {
  name: '',
  subject: '',
  body: '',
};

const EmailTemplates: React.FC = () => {
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const [editingTemplate, setEditingTemplate] = useState<EmailTemplate | null>(null);
  const [formData, setFormData] = useState<EmailTemplateFormData>(INITIAL_FORM_DATA);
  const [isSeeding, setIsSeeding] = useState(false);

  const { toast } = useToast();
  const { data: rawTemplates = [], isLoading } = useEmailTemplates();
  const templates = rawTemplates as EmailTemplate[];

  const createTemplate = useCreateEmailTemplate();
  const updateTemplate = useUpdateEmailTemplate();
  const deleteTemplate = useDeleteEmailTemplate();

  const {
    searchTerm,
    setSearchTerm,
    sortBy,
    setSortBy,
    filterType,
    setFilterType,
    sortedTemplates,
    clearFilters,
  } = useTemplateFilterSort({ templates });

  const resetForm = () => {
    setFormData(INITIAL_FORM_DATA);
    setEditingTemplate(null);
  };

  const handleOpenCreateModal = () => {
    resetForm();
    setIsCreateModalOpen(true);
  };

  const handleEditTemplate = (template: EmailTemplate) => {
    setFormData({
      name: template.name,
      subject: template.subject,
      body: template.body,
    });
    setEditingTemplate(template);
    setIsCreateModalOpen(true);
  };

  const handleSaveTemplate = async () => {
    if (!formData.name.trim() || !formData.subject.trim() || !formData.body.trim()) {
      toast({
        title: 'Missing Information',
        description: 'Please fill in all fields.',
        variant: 'destructive',
      });
      return;
    }

    try {
      if (editingTemplate) {
        await updateTemplate.mutateAsync({
          id: editingTemplate.id,
          ...formData,
        });
      } else {
        await createTemplate.mutateAsync(formData);
      }

      resetForm();
      setIsCreateModalOpen(false);
    } catch (error) {
      console.error('Error saving template:', error);
    }
  };

  const handleDeleteTemplate = async (templateId: string) => {
    try {
      await deleteTemplate.mutateAsync(templateId);
    } catch (error) {
      console.error('Error deleting template:', error);
    }
  };

  const handleLoadSampleTemplates = async () => {
    setIsSeeding(true);
    try {
      for (const sample of SAMPLE_TEMPLATES) {
        await createTemplate.mutateAsync(sample);
      }
      toast({
        title: 'Sample Templates Loaded',
        description: '9 sample templates have been added successfully.',
      });
    } catch (error) {
      console.error('Error seeding templates:', error);
      toast({
        title: 'Error Seeding Templates',
        description: 'Failed to load sample templates. Please try again.',
        variant: 'destructive',
      });
    } finally {
      setIsSeeding(false);
    }
  };

  return (
    <div className="space-y-6">
      {/* Search and Action Row */}
      <TemplateHeaderControls
        searchTerm={searchTerm}
        onSearchChange={setSearchTerm}
        sortBy={sortBy}
        onSortChange={setSortBy}
        filterType={filterType}
        onFilterChange={setFilterType}
        onNewTemplateClick={handleOpenCreateModal}
      />

      {/* Slide-out Sheet for Create / Edit */}
      <TemplateEditorSheet
        isOpen={isCreateModalOpen}
        onOpenChange={(open) => {
          setIsCreateModalOpen(open);
          if (!open) resetForm();
        }}
        editingTemplate={editingTemplate}
        formData={formData}
        onFormDataChange={setFormData}
        onSave={handleSaveTemplate}
        isSaving={createTemplate.isPending || updateTemplate.isPending}
      />

      {/* Loading state */}
      {isLoading && (
        <LoadingSpinner
          size="large"
          className="py-24 w-full flex justify-center"
          iconClassName="text-[#A655F8]"
        />
      )}

      {/* Templates Grid */}
      {!isLoading && sortedTemplates.length > 0 && (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {sortedTemplates.map((template) => (
            <TemplateCard
              key={template.id}
              template={template}
              onEdit={handleEditTemplate}
              onDelete={handleDeleteTemplate}
              isDeleting={deleteTemplate.isPending}
            />
          ))}
        </div>
      )}

      {/* Empty State */}
      {!isLoading && sortedTemplates.length === 0 && (
        <TemplateEmptyState
          searchTerm={searchTerm}
          filterType={filterType}
          isSeeding={isSeeding}
          onClearFilters={clearFilters}
          onCreateClick={handleOpenCreateModal}
          onLoadSamplesClick={handleLoadSampleTemplates}
        />
      )}
    </div>
  );
};

export default EmailTemplates;
